// Push notifications native (Android) — solo Verdetto Settimanale.
// Il centro notifiche interno resta invariato; qui si gestiscono token FCM, tap sulla notifica
// e la scelta del suono (un canale Android per suono: Android non permette di cambiare
// il suono di un canale esistente, quindi ogni suono ha il suo canale dedicato).
import { Capacitor } from "@capacitor/core";
import { api } from "./api";

export const isNativeApp = () => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
};

export const VERDETTO_CHANNEL_ID = "verdetto_settimanale";
export const REGALI_CHANNEL_ID = "regali_crediti";
export const ADMIN_CHANNEL_ID = "amministrazione";

// Suoni del Verdetto tra cui l'utente sceglie nelle Impostazioni (solo app Android).
// Predefinito: Sussurro (scelta della proprietaria) — il canale base usa quel suono.
// I file audio: android/app/src/main/res/raw/verdetto_<id>.mp3 (notifica) e
// frontend/public/sounds/verdetto_<id>.mp3 (anteprima). Deve restare allineato a VALID_PUSH_SOUNDS nel backend.
export const PUSH_SOUNDS = [
  { id: "default", label: "Sussurro — predefinito" },
  { id: "campana", label: "Campana" },
  { id: "campanella", label: "Campanella" },
  { id: "eco", label: "Eco" },
  { id: "celeste", label: "Celeste" },
];

export async function initPush(onOpenRoute) {
  try {
    if (!Capacitor.isNativePlatform()) return;
    const { PushNotifications } = await import("@capacitor/push-notifications");

    // Canale predefinito (suono Sussurro, scelto dalla proprietaria) + un canale per ogni altro suono.
    // La scelta dell'utente viene conservata da Android e non viene mai sovrascritta dall'app.
    try {
      await PushNotifications.createChannel({
        id: VERDETTO_CHANNEL_ID,
        name: "Verdetto Settimanale",
        description: "Avviso quando il nuovo Verdetto Settimanale è pronto ne La mia memoria",
        importance: 4,
        visibility: 1,
        sound: "verdetto_sussurro",
      });
    } catch { /* canale già esistente */ }
    for (const s of PUSH_SOUNDS) {
      if (s.id === "default") continue;
      try {
        await PushNotifications.createChannel({
          id: `${VERDETTO_CHANNEL_ID}_${s.id}`,
          name: `Verdetto Settimanale · ${s.label}`,
          description: "Avviso quando il nuovo Verdetto Settimanale è pronto ne La mia memoria",
          importance: 4,
          visibility: 1,
          sound: `verdetto_${s.id}`,
        });
      } catch { /* canale già esistente o suono non ancora presente nel build */ }
    }

    // Canale dei crediti regalati (notifica automatica di Arcana dopo l'assegnazione)
    try {
      await PushNotifications.createChannel({
        id: REGALI_CHANNEL_ID,
        name: "Regali di Arcana",
        description: "Avviso quando Arcana ti regala crediti per le tue consultazioni",
        importance: 4,
        visibility: 1,
      });
    } catch { /* canale già esistente */ }

    // Canale delle notifiche amministrative (solo titolare): identità sonora distinta (Campana)
    try {
      await PushNotifications.createChannel({
        id: ADMIN_CHANNEL_ID,
        name: "Arcana Admin",
        description: "Avvisi riservati alla titolare: registrazioni, abbonamenti, recensioni, segnalazioni",
        importance: 5,
        visibility: 1,
        sound: "verdetto_campana",
      });
    } catch { /* canale già esistente */ }

    // I listener vanno attaccati PRIMA di register(): altrimenti, se il token arriva
    // subito (cache FCM), l'evento "registration" si perde e il token non arriva mai al server.
    await PushNotifications.addListener("registration", async (token) => {
      try { await api.post("/push/register", { token: token.value, platform: "android" }); } catch { /* offline: riproverà al prossimo avvio */ }
    });
    await PushNotifications.addListener("pushNotificationActionPerformed", (ev) => {
      const route = ev.notification && ev.notification.data && ev.notification.data.route;
      if (route && onOpenRoute) onOpenRoute(route);
    });

    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== "granted") return;
    await PushNotifications.register();
  } catch { /* push non disponibile su questo dispositivo/build */ }
}

// --- scelta del suono (preferenza salvata sull'account, vale per l'app) ---
export async function getPushSound() {
  try {
    const r = await api.get("/push/sound");
    return r.data.sound || "default";
  } catch { return "default"; }
}

export async function savePushSound(sound) {
  await api.post("/push/sound", { sound });
}

// --- interruttore generale notifiche push (scelta dell'utente nelle Impostazioni) ---
export async function getPushEnabled() {
  try {
    const r = await api.get("/push/enabled");
    return r.data.enabled !== false;
  } catch { return true; }
}

export async function savePushEnabled(enabled) {
  await api.post("/push/enabled", { enabled });
}

let previewAudio = null;
export function previewPushSound(id) {
  const file = id === "default" ? "sussurro" : id; // il predefinito è Sussurro
  try {
    if (previewAudio) previewAudio.pause();
    previewAudio = new Audio(`/sounds/verdetto_${file}.mp3`);
    previewAudio.play().catch(() => {});
  } catch { /* anteprima non disponibile */ }
}
